import { locationIdFormat, productIdFormat } from "@redotech/redo-model/ids";
import { InventoryAdjustmentKind } from "@redotech/redo-prisma-client/enums";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

/**
 * Individual adjustment item (child row in expandable table)
 */
const AdjustmentHistoryItemSchema_ = z.object({
  id: z.string(), // BigInt ID as string
  productId: productIdFormat.schema,
  productTitle: z.string(),
  productSku: z.string().nullable(),
  productImage: z.string().nullable(),
  locationId: locationIdFormat.schema,
  locationName: z.string(),
  quantityDelta: z.number(), // Change in on-hand
  committedDelta: z.number(), // Change in committed
  notes: z.string().nullable(),
  createdAt: z.string(),
});

export const AdjustmentHistoryItemSchema: z.ZodType<AdjustmentHistoryItem> =
  AdjustmentHistoryItemSchema_;
export interface AdjustmentHistoryItem
  extends z.infer<typeof AdjustmentHistoryItemSchema_> {}

/**
 * Grouped adjustment (parent row in expandable table)
 * Groups adjustments by reference_id when non-null
 */
const AdjustmentHistoryGroupSchema_ = z.object({
  // Group identifier (reference_id or first adjustment id for ungrouped)
  groupKey: z.string(),
  // First item for summary display
  firstProductTitle: z.string(),
  firstProductSku: z.string().nullable(),
  firstProductImage: z.string().nullable(),
  // Activity type
  kind: createEnum(InventoryAdjustmentKind),
  kindDisplay: z.string(), // Human-readable label
  // Aggregated deltas across all items in group
  totalQuantityDelta: z.number(),
  totalCommittedDelta: z.number(),
  // Location info (all items in a group share same location typically)
  locationId: locationIdFormat.schema,
  locationName: z.string(),
  // Notes from first item (or aggregated if needed)
  notes: z.string().nullable(),
  // Timestamp (earliest in group)
  createdAt: z.string(),
  // Reference ID if present
  referenceId: z.string().nullable(),
  // Number of items in this group (for "and X more" display)
  itemCount: z.number(),
  // Child items (populated when expanded or always included)
  items: z.array(AdjustmentHistoryItemSchema),
});

export const AdjustmentHistoryGroupSchema: z.ZodType<AdjustmentHistoryGroup> =
  AdjustmentHistoryGroupSchema_;
export interface AdjustmentHistoryGroup
  extends z.infer<typeof AdjustmentHistoryGroupSchema_> {}

export const listAdjustmentHistory = {
  name: "listAdjustmentHistory" as const,
  input: z.object({
    search: z.string().optional(),
    locationId: locationIdFormat.schema.optional(),
    kind: createEnum(InventoryAdjustmentKind).optional(),
    startDate: z.string().optional(), // ISO date string
    endDate: z.string().optional(), // ISO date string
    sortDirection: z.enum(["asc", "desc"]).default("desc"),
    limit: z.number().default(50),
    offset: z.number().default(0),
  }),
  output: z.object({
    groups: z.array(AdjustmentHistoryGroupSchema),
    total: z.number(),
  }),
};

export { InventoryAdjustmentKind } from "@redotech/redo-prisma-client/enums";
